import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Clock, FileText } from 'lucide-react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useOnboarding } from '../contexts/OnboardingContext';

/**
 * NdaSigningReturn
 *
 * DocuSign redirects the client here after the embedded signing session ends.
 * Query params provided by DocuSign:
 *   event       — signing_complete | decline | cancel | session_timeout | ttl_expired
 *   envelopeId  — the DocuSign envelope ID (we append this to the returnUrl)
 *
 * On signing_complete:
 *   - Mark onboarding NDA step as complete locally (webhook will confirm fully)
 *   - Update user startFlowProgress
 *   - Navigate to dashboard
 *
 * On decline / cancel:
 *   - Show an explanation and offer to go back to /start/nda
 */
const NdaSigningReturn = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { darkMode } = useTheme();
  const { isOnboardingMode, markStepComplete } = useOnboarding();

  const [status, setStatus] = useState('processing'); // 'processing' | 'complete' | 'declined' | 'error'
  const [message, setMessage] = useState('');

  useEffect(() => {
    const event = searchParams.get('event');
    const envelopeId = searchParams.get('envelopeId');

    const handleReturn = async () => {
      if (!currentUser) {
        setStatus('error');
        setMessage('You must be logged in to complete this step.');
        return;
      }

      if (event === 'signing_complete') {
        try {
          // Get existing startFlowProgress so we don't overwrite other completed steps
          const userRef = doc(db, 'users', currentUser.uid);
          const userSnap = await getDoc(userRef);
          let existingCompletedSteps = [];
          if (userSnap.exists()) {
            const data = userSnap.data();
            existingCompletedSteps = data.startFlowProgress?.completedSteps || [];
          }

          const updatedSteps = [...new Set([...existingCompletedSteps, 1])];

          await updateDoc(userRef, {
            // Mark step as pending BBA counter-signature — the webhook will set it fully complete
            'onboardingStatus.steps.nda.pendingCounterSign': true,
            'pendingNda.signingComplete': true,
            'pendingNda.clientSignedAt': new Date().toISOString(),
            startFlowProgress: {
              completedSteps: updatedSteps,
              lastUpdated: new Date().toISOString()
            },
            lastUpdated: new Date().toISOString()
          });

          // Mark step in onboarding context so the sidebar updates immediately
          if (isOnboardingMode) {
            markStepComplete('nda');
          }

          setStatus('complete');
          setMessage('Your NDA has been signed successfully. Our team will counter-sign shortly and you\'ll receive a copy by email.');

          // Navigate to dashboard after a short delay
          setTimeout(() => {
            navigate('/start/dashboard');
          }, 3500);
        } catch (err) {
          console.error('Error updating NDA status after signing:', err);
          // Even if Firestore update fails, navigate to dashboard — the webhook will reconcile
          setStatus('complete');
          setMessage('Your signature was received. You will be redirected shortly.');
          setTimeout(() => navigate('/start/dashboard'), 3500);
        }
      } else if (event === 'decline') {
        setStatus('declined');
        setMessage('You declined to sign the NDA. You can review the document and try again whenever you\'re ready.');
      } else if (event === 'cancel') {
        setStatus('declined');
        setMessage('You cancelled the signing session. You can return to the NDA to try again.');
      } else if (event === 'session_timeout' || event === 'ttl_expired') {
        setStatus('error');
        setMessage('Your signing session expired. Please return to the NDA and start again.');
      } else {
        // Unexpected event — just go to dashboard
        navigate('/start/dashboard');
      }
    };

    handleReturn();
  }, [searchParams, currentUser, navigate, isOnboardingMode, markStepComplete]);

  return (
    <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className={`max-w-md w-full mx-4 rounded-xl shadow-lg p-8 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="text-center">

          {status === 'processing' && (
            <>
              <div className="flex justify-center mb-4">
                <Clock className={`h-14 w-14 ${darkMode ? 'text-blue-400' : 'text-blue-500'}`} />
              </div>
              <h2 className={`text-xl font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Processing your signature…
              </h2>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Please wait a moment.
              </p>
            </>
          )}

          {status === 'complete' && (
            <>
              <div className="flex justify-center mb-4">
                <CheckCircle className="h-14 w-14 text-green-500" />
              </div>
              <h2 className={`text-xl font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                NDA Signed!
              </h2>
              <p className={`text-sm mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {message}
              </p>
              <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Redirecting you to the dashboard…
              </div>
            </>
          )}

          {(status === 'declined') && (
            <>
              <div className="flex justify-center mb-4">
                <FileText className={`h-14 w-14 ${darkMode ? 'text-yellow-400' : 'text-yellow-500'}`} />
              </div>
              <h2 className={`text-xl font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Signing Not Completed
              </h2>
              <p className={`text-sm mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {message}
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => navigate('/start/nda')}
                  className="w-full px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
                >
                  Return to NDA
                </button>
                <button
                  onClick={() => navigate('/start/dashboard')}
                  className={`w-full px-4 py-2 rounded-lg font-medium transition-colors ${
                    darkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Go to Dashboard
                </button>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="flex justify-center mb-4">
                <AlertCircle className="h-14 w-14 text-red-500" />
              </div>
              <h2 className={`text-xl font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Something Went Wrong
              </h2>
              <p className={`text-sm mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {message}
              </p>
              <button
                onClick={() => navigate('/start/nda')}
                className="w-full px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
              >
                Return to NDA
              </button>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default NdaSigningReturn;
